/**
 * 「把这个元素换成另一种形状」—— 形状工作台的唯一写入口。
 *
 * 三条硬约定（都是本项目踩过的坑换来的）：
 *
 * 1. **原位替换**：新元素插回**同一个父节点的同一个索引**。父级可能是
 *    `<g clip-path="url(#…)">`（plotly 的散点就在里面），挪位置会让裁剪失效、层序错乱。
 *
 * 2. **属性逐条搬 + 几何属性整体丢弃**：`fill / stroke / opacity / fill-opacity /
 *    transform / class / data-editor-locked …` 全部原样保留（**transform 保留 = 旋转与嵌套组自动正确**），
 *    只丢掉几何类属性（每个标签的几何属性集合不同，混着留会互相打架）。
 *    原来没有 id 就不加 `ext-N` —— 别往用户的文件里塞标识。
 *
 * 3. **闭包 custom patch + 路径寻址**：结构类改动不能用容器快照（会让选中集/列表持有的
 *    引用变游离节点，见 patch.ts 的说明）；apply/revert 按 `{父路径, 索引}` 现场定位并重建，
 *    两者都是幂等的（已经是目标状态就不动 DOM），所以撤销→重做来回都稳。
 */
import { SVG_NS, nodeAt, pathOf, type Path } from '../dom';
import type { Command } from '../history';
import { makeCustomPatch, type Patch } from '../patch';
import { isConvertibleTag, localArea, localBBox } from '../geom/local-bbox';
import { SHAPE_LABEL, shapeGeometry, type FitMode, type ShapeKind } from '../shapes';

/** 换形状时必须整体丢弃的几何属性（各标签的集合不同，留着会互相打架） */
const GEOM_ATTRS = new Set(['x', 'y', 'width', 'height', 'rx', 'ry', 'cx', 'cy', 'r', 'x1', 'y1', 'x2', 'y2', 'points', 'd']);

const NS_XLINK = 'http://www.w3.org/1999/xlink';

export type AttrPairs = Array<[string, string]>;

/** 元素自身的属性（保序 —— 写回去时作者的书写顺序不变） */
export function attrsOf(el: Element): AttrPairs {
  return Array.from(el.attributes, (a) => [a.name, a.value] as [string, string]);
}

/** 状态签名（名字排序 → 与属性在源码里的先后无关），幂等判断用 */
export function signatureOf(tag: string, attrs: AttrPairs): string {
  const sorted = [...attrs].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return `${tag}|${sorted.map(([n, v]) => `${n}=${v}`).join(';')}`;
}

function makeElement(doc: Document, tag: string, attrs: AttrPairs): Element {
  const el = doc.createElementNS(SVG_NS, tag);
  for (const [name, value] of attrs) {
    if (name.startsWith('xlink:')) el.setAttributeNS(NS_XLINK, name, value);
    else el.setAttribute(name, value);
  }
  return el;
}

export interface ShapeSwap {
  /** 父容器路径 */
  parent: Path;
  /** 在父容器 children 里的索引 */
  index: number;
  fromTag: string;
  from: AttrPairs;
  toTag: string;
  to: AttrPairs;
  /** 两个方向的状态签名（"原样"与"目标态"）—— 用来确认那个索引上还是我们记的元素 */
  fromSig: string;
  toSig: string;
}

/**
 * 按路径 + 索引现场换掉元素。
 *
 * 两道守卫：
 *  · 已是目标状态 → 不动（幂等，apply/revert 反复调用都稳）；
 *  · 当前位置上的元素**既不是原样也不是目标态**（比如中间被别人删过、索引被别的元素顶上）→
 *    静默跳过。宁可什么都不做，也不能把不相干的兄弟元素当成它换掉。
 */
function swapAt(
  root: Element,
  s: ShapeSwap,
  tag: string,
  attrs: AttrPairs,
  onSwap?: (oldEl: Element, newEl: Element) => void,
): boolean {
  const parent = nodeAt(root, s.parent);
  const cur = parent?.children[s.index];
  if (!parent || !cur) return false;
  const sig = signatureOf(cur.tagName.toLowerCase(), attrsOf(cur));
  if (sig === signatureOf(tag, attrs)) return false;
  if (sig !== s.fromSig && sig !== s.toSig) return false;
  const next = makeElement(cur.ownerDocument, tag, attrs);
  parent.replaceChild(next, cur);
  onSwap?.(cur, next);
  return true;
}

export interface ShapeReplaceResult {
  cmd: Command;
  /** 真正会被改的元素（顺序与传入一致；调用方用它记账/提示） */
  changed: Element[];
  /** 跳过的元素与原因（文本/图片/退化元素/算不出框） */
  skipped: Array<{ element: Element; reason: string }>;
}

export interface ShapeReplaceOptions {
  fit?: FitMode;
  /** 面板用：让调用方能显示「已跳过 N 个（文本/图片等）」 */
  withReport?: boolean;
  /**
   * **新旧节点的身份交接**（apply 与 revert / redo 都会调，方向一致）。
   *
   * 换形状是**重建节点**（`replaceChild`），不是改属性 —— 于是调用方按**节点身份**记的东西
   * 会当场失效：编辑器里的「本次新建」标记（WeakSet）、选中集、悬停高亮……
   * 所以这里把「旧节点 → 刚写进去的新节点」一一交给调用方，让它自己续接。
   * 不交出去就只能靠调用方重新查 DOM，那样撤销 / 重做这两条路都会漏。
   */
  onSwap?: (oldEl: Element, newEl: Element) => void;
}

/** 内部分析：算出这一次要做的全部替换（构造阶段零副作用，只读 DOM） */
function plan(root: Element, els: Iterable<Element>, kind: ShapeKind, opts: ShapeReplaceOptions) {
  const swaps: ShapeSwap[] = [];
  const changed: Element[] = [];
  const skipped: Array<{ element: Element; reason: string }> = [];

  for (const el of els) {
    const tag = el.tagName.toLowerCase();
    if (!isConvertibleTag(tag)) {
      skipped.push({ element: el, reason: '这类元素没有几何形状（文本 / 图片 / 组）' });
      continue;
    }
    const parent = el.parentElement;
    if (!parent) {
      skipped.push({ element: el, reason: '不在文档树里' });
      continue;
    }
    const box = localBBox(el);
    if (!box) {
      skipped.push({ element: el, reason: '算不出包围盒（路径需要真实渲染环境）' });
      continue;
    }
    if (box.w <= 0 || box.h <= 0) {
      skipped.push({ element: el, reason: '退化元素（宽或高为 0，例如水平/垂直直线）' });
      continue;
    }
    const geo = shapeGeometry(kind, box, { fit: opts.fit ?? 'bbox', sourceArea: localArea(el) });
    if (!geo) {
      skipped.push({ element: el, reason: '这个尺寸下没法生成该形状' });
      continue;
    }
    const parentPath = pathOf(parent, root);
    if (!parentPath) {
      skipped.push({ element: el, reason: '父节点不在文档树内' });
      continue;
    }
    const index = Array.prototype.indexOf.call(parent.children, el);
    if (index < 0) {
      skipped.push({ element: el, reason: '找不到元素在父节点里的位置' });
      continue;
    }
    const from = attrsOf(el);
    const to: AttrPairs = [...from.filter(([name]) => !GEOM_ATTRS.has(name)), ...Object.entries(geo.attrs)];
    if (signatureOf(tag, from) === signatureOf(geo.tag, to)) continue; // 已经是这个形状 → 不算改动
    swaps.push({
      parent: parentPath,
      index,
      fromTag: tag,
      from,
      toTag: geo.tag,
      to,
      fromSig: signatureOf(tag, from),
      toSig: signatureOf(geo.tag, to),
    });
    changed.push(el);
  }
  return { swaps, changed, skipped };
}

/**
 * 把一批元素换成同一种形状。**一批 = 一条命令 = 一次 Ctrl+Z**。
 * 没有任何改动时返回 null（空操作绝不进历史栈）。
 */
export function replaceShapes(root: Element, els: Iterable<Element>, kind: ShapeKind, opts: ShapeReplaceOptions = {}): ShapeReplaceResult | null {
  const { swaps, changed, skipped } = plan(root, els, kind, opts);
  if (swaps.length === 0) return null;

  const patch: Patch = makeCustomPatch(
    `改为${SHAPE_LABEL[kind]}`,
    () => {
      for (const s of swaps) swapAt(root, s, s.toTag, s.to, opts.onSwap);
    },
    () => {
      // 倒序还原：同一父节点里有多个替换时，按构造顺序的逆序退更直观稳定
      for (let i = swaps.length - 1; i >= 0; i--) {
        const s = swaps[i] as ShapeSwap;
        swapAt(root, s, s.fromTag, s.from, opts.onSwap);
      }
    },
    swaps.reduce((sum, s) => sum + s.from.reduce((n, [, v]) => n + v.length, 0) * 2, 0),
  );

  return { cmd: { label: `${swaps.length} 个元素改为${SHAPE_LABEL[kind]}`, patches: [patch] }, changed, skipped };
}
